import { Fragment } from 'react'
import { isConsole } from 'shared/config/config'
import { User } from 'shared/models/user'

import { Context, OnboardingChecklistName, OnboardingChecklistStatusDetail } from '../../models/onboarding'
import { StyledCard, StyledSubTasks } from './TaskList.styles'
import { TaskListContent } from './TaskListContent'
import { TaskListOnboardingInsurance } from './TaskListOnboardingInsurance'
import { TaskListSubTask } from './TaskListSubTask'

export enum TaskStatusEnum {
  NOT_STARTED = 'NOT_STARTED',
  PREREQUISITES_NOT_MET = 'PREREQUISITES_NOT_MET',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS',
}

export const TaskStatusEnumLabel = {
  [TaskStatusEnum.NOT_STARTED]: 'Not started',
  [TaskStatusEnum.PREREQUISITES_NOT_MET]: 'Prerequisites not met',
  [TaskStatusEnum.COMPLETED]: 'Completed',
  [TaskStatusEnum.ERROR]: 'Error',
  [TaskStatusEnum.IN_PROGRESS]: 'In progress',
}

export interface Task {
  key?: string
  name: OnboardingChecklistName
  title: string | null
  titleRenderer?: React.ReactNode
  subTitle?: React.ReactNode
  status: TaskStatusEnum
  statusText: string
  statusDetail?: OnboardingChecklistStatusDetail
  statusAdditionalText: React.ReactNode | React.ReactNode[]
  subTasks?: ReadonlyArray<SubTask>
  isExpanded?: boolean
  ctaText?: React.ReactNode | string | null
  onCtaClick?: () => void
  ctaRenderer?: React.ReactNode
  isDisabled?: boolean
  context?: Context
}

export type SubTask = Omit<Task, 'subTasks' | 'isExpanded'>

export interface TaskListProps {
  tasks: ReadonlyArray<Task>
  isReadOnly?: boolean
  user?: User
}

interface TaskListContentWrapperProps {
  task: Task
  isReadOnly?: boolean
  user?: User
}

const TaskListContentWrapper: React.FC<TaskListContentWrapperProps> = ({ task, isReadOnly, user }) => {
  // Refactor @nyu GRO-422
  if (task.name === OnboardingChecklistName.INSURANCE_SETUP && !task.isDisabled && task.subTasks?.length) {
    if (isConsole()) {
      return (
        <Fragment key={`${task.title}-${task.statusText}-${task.key}`}>
          <TaskListContent task={task} isReadOnly={isReadOnly} />
          <TaskListOnboardingInsurance task={task} isReadOnly={isReadOnly} user={user} />
        </Fragment>
      )
    } else {
      return <TaskListContent task={task} isReadOnly={isReadOnly} key={`${task.title}-${task.statusText}`} />
    }
  }

  if (task.subTasks?.length && !task.isDisabled) {
    return (
      <Fragment key={`${task.title}-${task.statusText}-${task.key}`}>
        <TaskListContent task={task} isReadOnly={isReadOnly} />
        <StyledSubTasks>
          {task.subTasks.map((t) => (
            <TaskListSubTask
              key={`${t.title}-${t.statusText}-${t.key}`}
              subTask={t}
              isReadOnly={isReadOnly}
            />
          ))}
        </StyledSubTasks>
      </Fragment>
    )
  }

  return <TaskListContent task={task} isReadOnly={isReadOnly} key={`${task.title}-${task.statusText}`} />
}

export const TaskList: React.FC<TaskListProps> = ({ tasks, isReadOnly, user }) => {
  // Fragment needed for storybook
  return (
    <>
      {tasks.map((task) => (
        <StyledCard className="tasklist-card" key={`${task.title}-${task.statusText}`}>
          <TaskListContentWrapper task={task} isReadOnly={isReadOnly} user={user} />
        </StyledCard>
      ))}
    </>
  )
}
